import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref } from 'vue'

  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import ProfileQuestion from '@/components/ProfileQuestion.vue'
  import LanguageTranslateToggle from '@/components/common/LanguageTranslateToggle.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import AcademyProfileUpdateFormLoader from '@/components/loaders/AcademyProfileUpdateFormLoader.vue'

  import { useRoute, useRouter } from 'vue-router/composables'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useIcons from '@/composables/useIcons'
  import useSegment from '@/composables/useSegment'
  import useI18n from '@/composables/useI18n'
  import useCourse from '@/composables/useCourse'
  import useAuthentication from '@/composables/useAuthentication'
  import useBreakpoint from '@/composables/useBreakpoint'

  import { UserModule } from '@/store/modules/user'
  import { CourseModule } from '@/store/modules/course'

  import UsersApi from '@/services/api/UsersApi'
  import CourseApi from '@/services/api/CourseApi'
  import CookieHelper from '@/helpers/CookieHelper'

  import type { CompanyProfileQuestion, UserUtmParameters } from '@/services/interfaces/Auth0'
  import type { QuestionAnswer } from '@/services/interfaces/QuizQuestion'
  import type { Nullable } from '@/services/interfaces/Content'

  import * as Sentry from '@sentry/vue'
  import { DateTime } from 'luxon'
  import eventBus from '@/main'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'AcademyProfileUpdateForm',
  props: {
    firstName: {
      type: String,
      required: true,
    },
    lastName: {
      type: String,
      required: true,
    },
  },
  emits: ['loading-company-profile-questions', 'routing-to-next-onboarding-page'],
  setup(__props, { emit }) {

const props = __props;

  const route = useRoute()
  const router = useRouter()

  const {
    setPhoneOrTextAnswer,
    onAnswerChoiceClick,
    profileQuestionIsUnfilled,
    showTranslateSelector,
    languageTranslations,
    nextOnboardingPage,
    highlightedCourseBundlePrefix,
    fromEmbeddedQuiz,
    windowOpenProxyCheck,
    currentCompanyId,
    assignNewUserToCompany,
    hasValidUser,
  } = useCommonMixin()
  const { checkboxSquareChecked, checkboxSquare } = useIcons()
  const { frontendUserSignedUp, trackPage, pageViewed } = useSegment()
  const { translateString } = useI18n()
  const { hasPurchasedAtLeastACourse } = useCourse()
  const { tokenKey } = useAuthentication()
  const { isDesktopDevice } = useBreakpoint()

  

  

  const formUser: { firstName: string | null; lastName: string | null } = reactive({
    firstName: props.firstName || null,
    lastName: props.lastName || null,
  })
  const selectedAnswers = ref([] as QuestionAnswer[])
  const companyProfileQuestions = ref([] as CompanyProfileQuestion[])
  const mandatoryQuestionIds = ref([] as string[])
  const loadingCompanyProfileQuestions = ref(true)
  const selectedTermsAndPrivacy = ref(false)
  const acceptedLearningProgressTransfer = ref(false)
  const showErrorText = ref(false)
  const submittingProfileDetails = ref(false)
  const firstNameInput = ref(null as Nullable<HTMLInputElement>)

  const structuredCompanyProfileQuestions = computed(() => {
    return companyProfileQuestions.value?.map((companyProfileQuestion) => ({
      position: companyProfileQuestion.position,
      mandatory: companyProfileQuestion.mandatory,
      ...companyProfileQuestion.profileQuestion,
    }))
  })

  const mandatoryQuestionsFilled = computed(() => {
    return mandatoryQuestionIds.value.every((qId) => {
      const profileQuestion = structuredCompanyProfileQuestions.value.find((_q) => _q.id === qId)
      return !profileQuestionIsUnfilled.value(selectedAnswers.value, profileQuestion!)
    })
  })

  const hasNotFilledOutAllFields = computed((): boolean => {
    return !(formUser.firstName && formUser.lastName && selectedTermsAndPrivacy.value && mandatoryQuestionsFilled.value)
  })

  const showLearningProgressTransferConsent = computed(() => UserModule.currentCompany?.allowExportingLearningProgress)

  const learningProgressPolicyUrl = computed(() => UserModule.currentCompany?.policies?.learningProgressPolicyPdfUrl)

  const setAnswer = (questionId: string, text: string, answerType: 'phone' | 'text') => {
    setPhoneOrTextAnswer(selectedAnswers.value, questionId, text, answerType)
  }

  const submitProfile = async () => {
    submittingProfileDetails.value = true

    try {
      await UsersApi.updateOnboardingUser(
        {
          auth0Id: UserModule.auth0Id,
          firstName: formUser.firstName!,
          lastName: formUser.lastName!,
        },
        selectedAnswers.value,
        true,
        currentCompanyId.value,
        acceptedLearningProgressTransfer.value,
      )

      // Remove the cookie when we combine all signup flows
      CookieHelper.removeCrossDomainCookie('is-academy-signup')

      emit('routing-to-next-onboarding-page')

      if (window.opener) {
        window.opener.postMessage(CookieHelper.getCookieValue(tokenKey.value), '*')
      }

      frontendUserSignedUp(UserModule.email)
      await UserModule.getUser()

      submittingProfileDetails.value = false

      await nextAction()
    } catch (error) {
      submittingProfileDetails.value = false
      eventBus.$toasted.error(translateString('onboarding.profilePage.profileUpdateUnsuccessful'))
    }
  }

  const nextAction = async () => {
    const nextPage = nextOnboardingPage.value('ProfilePage')

    if (!hasPurchasedAtLeastACourse.value && nextPage) {
      if (UserModule.hasSelectedTrial) {
        /* users who go through the onboarding survey fall here
        since they've already been locked into a course trial bundle
        */
        window.location.href = '/'
        return
      }

      if (nextPage === 'ExamDatePicker') {
        // route to homepage since ExamDatePicker is a modal now
        window.location.href = '/'
      } else if (nextPage !== route.name) {
        if (fromEmbeddedQuiz.value) {
          await automaticallyAddTrialCourses()
          window.location.href = '/'

          return
        }

        const targetRoute: { name: string; query?: Record<string, string> } = {
          name: nextPage,
        }

        if (highlightedCourseBundlePrefix.value) {
          targetRoute.query = { bundlePrefix: highlightedCourseBundlePrefix.value }
        }

        router.push(targetRoute).catch(() => {
          return
        })
      }
    } else {
      window.location.href = '/'
    }
  }

  const automaticallyAddTrialCourses = async () => {
    try {
      const targetBundle = CourseModule.courseBundlesOverview?.find((bundle) =>
        bundle.title.includes(fromEmbeddedQuiz.value!),
      )

      const courseIds = targetBundle?.courses?.map((course) => course.id) ?? []

      if (courseIds.length === 0) return

      await CourseApi.startCourseTrial(courseIds)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const getCompanyProfileQuestions = () => {
    UsersApi.getCompanyProfileQuestions()
      .then((questions) => {
        companyProfileQuestions.value = questions
      })
      .finally(() => {
        loadingCompanyProfileQuestions.value = false
        emit('loading-company-profile-questions', false)
        setMandatoryQuestions()
        if (route.query.testDomain) {
          prefillSelectedAnswers()
        }
      })
  }

  const setMandatoryQuestions = () => {
    mandatoryQuestionIds.value =
      structuredCompanyProfileQuestions.value?.filter((q) => q.mandatory).map((_q) => _q.id) ?? []
  }

  const prefillSelectedAnswers = () => {
    // prefill for test user
    selectedAnswers.value = [
      {
        questionId: 'bd43b6a9-00af-4dac-8530-9d178ed3651a',
        selectedAnswerIds: ['2bcae7fb-0f96-4885-96b3-10e49e309449'],
      },
      {
        questionId: '9b2fb1a6-1377-4d1b-8741-0bf418f51da3',
        selectedAnswerIds: ['6fef97ec-f93c-4037-9140-ce1787968d35'],
      },
      {
        questionId: '23e620bb-565e-4fda-9397-03ae40d77dd6',
        selectedAnswerIds: ['5a94ba03-0d86-4a94-84f9-ea975bc12ee2'],
      },
    ]
  }

  const showLearningProgressPolicy = () => {
    if (!learningProgressPolicyUrl.value) {
      acceptedLearningProgressTransfer.value = !acceptedLearningProgressTransfer.value
      return
    }

    if (isDesktopDevice.value) {
      eventBus.$emit('show-modal', {
        modalContentComponent: 'LearningProgressPolicyModal',
        modalProps: {
          policyUrl: learningProgressPolicyUrl.value,
        },
        cssClass: '.learning-progress-policy-modal',
        modalCloseCallback: (callback: () => void) => {
          if (typeof callback === 'function') callback()
        },
      })
    } else {
      windowOpenProxyCheck(learningProgressPolicyUrl.value!, 'learning_progress_policy_pdf_viewing')
    }
  }

  const updateUserUtmParams = async () => {
    try {
      if (!UserModule.auth0Id) await UserModule.getUser()
      const extractedParams: { [key in UserUtmParameters]?: string } = {}

      const urlParams = new URLSearchParams(window.location.search)
      const userUtmParameters: UserUtmParameters[] = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'utm_lp',
        'utm_adgroup',
        'gclid',
        'gbraid',
        'wbraid',
        'msclkid',
      ]

      userUtmParameters.forEach((param) => {
        if (param === 'utm_lp') {
          /* Handle multiple utm_lp parameters
               and filter out the one with v1
             */
          const paramValues = urlParams.getAll(param)
          const validParamValues = paramValues.filter((value) => value !== 'v1')

          if (validParamValues.length > 0) {
            extractedParams[param] = decodeURIComponent(validParamValues[0])
          }
        } else {
          const paramValue = urlParams.get(param)
          if (paramValue) {
            extractedParams[param] = decodeURIComponent(paramValue)
          }
        }
      })

      if (Object.keys(extractedParams).length > 0 && UserModule.auth0Id) {
        identifyUserInSegment(extractedParams)
        await UsersApi.updateUserUtmParameters(UserModule.auth0Id, extractedParams)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const identifyUserInSegment = (utmParameters: { [key in UserUtmParameters]?: string }): void => {
    const { analytics } = window as any
    if (analytics) {
      const identifyUserObject: any = {
        name: UserModule.fullname,
        email: UserModule.email,
        company: UserModule.currentCompany?.name || '',
        created_at: DateTime.fromSQL(UserModule.createdAt),
        ...utmParameters,
      }

      analytics.identify(UserModule.auth0Id, identifyUserObject)
    }
  }

  const trackPageViewed = () => {
    if (hasValidUser.value && !UserModule.onboarded) {
      if (route.name === 'CustomRegistration') {
        trackPage('Academy Profile Page')
        pageViewed('Academy Profile Page')
      } else {
        trackPage('Profile Page')
        pageViewed('Profile Page')
      }
    }
  }

  onMounted(() => {
    if (route.query.accessToken && !CookieHelper.getCookieValue(tokenKey.value)) {
      CookieHelper.setCrossDomainCookie(tokenKey.value, route.query.accessToken as string)
    }

    assignNewUserToCompany()
    trackPageViewed()
    getCompanyProfileQuestions()
    updateUserUtmParams()
  })

return { __sfc: true,route, router, setPhoneOrTextAnswer, onAnswerChoiceClick, profileQuestionIsUnfilled, showTranslateSelector, languageTranslations, nextOnboardingPage, highlightedCourseBundlePrefix, fromEmbeddedQuiz, windowOpenProxyCheck, currentCompanyId, assignNewUserToCompany, hasValidUser, checkboxSquareChecked, checkboxSquare, frontendUserSignedUp, trackPage, pageViewed, translateString, hasPurchasedAtLeastACourse, tokenKey, isDesktopDevice, props, emit, formUser, selectedAnswers, companyProfileQuestions, mandatoryQuestionIds, loadingCompanyProfileQuestions, selectedTermsAndPrivacy, acceptedLearningProgressTransfer, showErrorText, submittingProfileDetails, firstNameInput, structuredCompanyProfileQuestions, mandatoryQuestionsFilled, hasNotFilledOutAllFields, showLearningProgressTransferConsent, learningProgressPolicyUrl, setAnswer, submitProfile, nextAction, automaticallyAddTrialCourses, getCompanyProfileQuestions, setMandatoryQuestions, prefillSelectedAnswers, showLearningProgressPolicy, updateUserUtmParams, identifyUserInSegment, trackPageViewed, computed, onMounted, reactive, ref, KetchUpButton, LoadingSpinner, ProfileQuestion, LanguageTranslateToggle, SVGRenderer, AcademyProfileUpdateFormLoader, useRoute, useRouter, useCommonMixin, useIcons, useSegment, useI18n, useCourse, useAuthentication, useBreakpoint, UserModule, CourseModule, UsersApi, CourseApi, CookieHelper, Sentry, DateTime, eventBus }
}

})